import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { AuthContext } from '../../../../App'
import CatalogTaxonomyNodeList from './CatalogTaxonomyNodeList/CatalogTaxonomyNodeList'
import CatalogTaxonomyNodeEditor from './CatalogTaxonomyNodeEditor/CatalogTaxonomyNodeEditor'
import { COLUMN_TITLES, NODE_TYPE, CATALOG_TAXONOMY_COLLECTION } from './catalogTaxonomyConstants'
import {
  partitionSiblingLists,
  subscribeCatalogTaxonomySiblings,
} from './catalogTaxonomyApi'
import { getCreateChildPolicy } from './catalogTaxonomyEffectiveState'
import getDoc from '../../../../utils/db/getDoc'
import getCatalogTaxonomyProductCounts from '../../../../api/products/getCatalogTaxonomyProductCounts'
import Button from '../../../../ui/Button/Button'
import styles from './CatalogTaxonomy.module.scss'

const CATEGORY_TREE_BASE = '/products/category-tree'

const EMPTY_PATH = { globalId: '', categoryId: '' }

function pathFromNode(node, ancestorGlobalId = '') {
  if (!node) return EMPTY_PATH
  if (node.type === NODE_TYPE.GLOBAL) {
    return { globalId: node.id, categoryId: '' }
  }
  if (node.type === NODE_TYPE.CATEGORY) {
    return { globalId: node.parentId || '', categoryId: node.id }
  }
  return {
    globalId: ancestorGlobalId || '',
    categoryId: node.parentId || '',
  }
}

function formatCountsUpdatedAt(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function CatalogTaxonomy({ ...router }) {
  const { setPageTitle } = useContext(AuthContext)
  const selectedId = router.match.params.nodeId || ''

  const [selectedNode, setSelectedNode] = useState(null)
  const [pathIds, setPathIds] = useState(EMPTY_PATH)
  const [pathGlobal, setPathGlobal] = useState(null)
  const [pathCategory, setPathCategory] = useState(null)
  const [createState, setCreateState] = useState(null)

  const [productCountsByNodeId, setProductCountsByNodeId] = useState({})
  const [productCountsStatus, setProductCountsStatus] = useState('idle')
  const [productCountsGeneratedAt, setProductCountsGeneratedAt] = useState(null)
  const [productCountsError, setProductCountsError] = useState(null)

  useEffect(() => {
    setPageTitle('Дерево категорій')
  }, [setPageTitle])

  const loadProductCounts = useCallback(async () => {
    setProductCountsStatus('loading')
    setProductCountsError(null)

    const result = await getCatalogTaxonomyProductCounts()
    if (!result.ok) {
      setProductCountsStatus('error')
      setProductCountsError(result.message || 'Не вдалося завантажити кількість товарів')
      return
    }

    setProductCountsByNodeId(result.data?.countsByNodeId || {})
    setProductCountsGeneratedAt(result.data?.generatedAt || null)
    setProductCountsStatus('ready')
  }, [])

  useEffect(() => {
    loadProductCounts()
  }, [loadProductCounts])

  useEffect(() => {
    if (selectedId || createState) return undefined

    return subscribeCatalogTaxonomySiblings(null, (data) => {
      const { nonArchived } = partitionSiblingLists(data)
      const first = nonArchived[0]
      if (first?.id) {
        router.history.replace(`${CATEGORY_TREE_BASE}/${first.id}`)
      }
    })
  }, [selectedId, createState, router.history])

  useEffect(() => {
    if (!selectedId || createState) {
      if (!selectedId) {
        setSelectedNode(null)
        setPathIds(EMPTY_PATH)
        setPathGlobal(null)
        setPathCategory(null)
      }
      return undefined
    }

    let cancelled = false

    getDoc({ path: CATALOG_TAXONOMY_COLLECTION, docId: selectedId, docIdName: 'id' }).then(async (data) => {
      if (cancelled) return
      if (!data) {
        setSelectedNode(null)
        setPathIds(EMPTY_PATH)
        setPathGlobal(null)
        setPathCategory(null)
        return
      }

      setSelectedNode(data)

      if (data.type === NODE_TYPE.GLOBAL) {
        setPathIds(pathFromNode(data))
        setPathGlobal(data)
        setPathCategory(null)
        return
      }

      if (data.type === NODE_TYPE.CATEGORY) {
        setPathIds(pathFromNode(data))
        setPathCategory(data)
        const global = data.parentId
          ? await getDoc({ path: CATALOG_TAXONOMY_COLLECTION, docId: data.parentId, docIdName: 'id' })
          : null
        if (cancelled) return
        setPathGlobal(global || null)
        return
      }

      if (data.type === NODE_TYPE.SUBCATEGORY && data.parentId) {
        const category = await getDoc({
          path: CATALOG_TAXONOMY_COLLECTION,
          docId: data.parentId,
          docIdName: 'id',
        })
        if (cancelled) return
        setPathIds(pathFromNode(data, category?.parentId || ''))
        setPathCategory(category || null)
        const global =
          category?.parentId
            ? await getDoc({
                path: CATALOG_TAXONOMY_COLLECTION,
                docId: category.parentId,
                docIdName: 'id',
              })
            : null
        if (cancelled) return
        setPathGlobal(global || null)
        return
      }

      setPathIds(pathFromNode(data))
      setPathGlobal(null)
      setPathCategory(null)
    })

    return () => {
      cancelled = true
    }
  }, [selectedId, createState])

  const selectNode = (id) => {
    setCreateState(null)
    router.history.push(`${CATEGORY_TREE_BASE}/${id}`)
  }

  const startCreate = (type, parentId, parentNode, parentAncestors = []) => {
    const policy = parentNode
      ? getCreateChildPolicy(parentNode, parentAncestors)
      : { allowed: true, code: 'ok', warning: null }

    if (!policy.allowed) {
      window.alert(policy.message)
      return
    }

    setCreateState({ type, parentId, parentNode, parentAncestors, createWarning: policy.warning })
  }

  const { globalId: selectedGlobalId, categoryId: selectedCategoryId } = pathIds
  const categoryParentReady = Boolean(selectedGlobalId)
  const subcategoryParentReady = Boolean(selectedCategoryId)
  const selectedSubcategoryId =
    selectedNode?.type === NODE_TYPE.SUBCATEGORY ? selectedId : ''

  const categoryAncestors = useMemo(() => (pathGlobal ? [pathGlobal] : []), [pathGlobal])
  const subcategoryAncestors = useMemo(
    () => [pathGlobal, pathCategory].filter(Boolean),
    [pathGlobal, pathCategory]
  )

  const selectedAncestors = useMemo(() => {
    if (!selectedNode) return []
    if (selectedNode.type === NODE_TYPE.GLOBAL) return []
    if (selectedNode.type === NODE_TYPE.CATEGORY) return categoryAncestors
    return subcategoryAncestors
  }, [selectedNode, categoryAncestors, subcategoryAncestors])

  const categoryCreatePolicy = useMemo(
    () => (pathGlobal ? getCreateChildPolicy(pathGlobal, []) : null),
    [pathGlobal]
  )
  const subcategoryCreatePolicy = useMemo(
    () => (pathCategory ? getCreateChildPolicy(pathCategory, categoryAncestors) : null),
    [pathCategory, categoryAncestors]
  )

  const countsLoading = productCountsStatus === 'loading'
  const updatedLabel = formatCountsUpdatedAt(productCountsGeneratedAt)

  return (
    <div className={styles.page}>
      <div className={styles.countsBar}>
        {productCountsStatus === 'error' ? (
          <p className={styles.countsError}>{productCountsError || 'Помилка завантаження лічильників'}</p>
        ) : (
          <p className={styles.countsMeta}>
            {countsLoading
              ? 'Оновлення кількості товарів…'
              : updatedLabel
                ? `Кількість товарів станом на ${updatedLabel}`
                : 'Кількість товарів'}
          </p>
        )}
        <Button
          className={styles.countsRefresh}
          theme="circle"
          fill="white"
          color={countsLoading ? 'white' : 'accent'}
          icon="sync"
          tooltip="Оновити кількість товарів"
          isLoading={countsLoading}
          onClick={loadProductCounts}
        />
      </div>

      <div className={styles.root}>
        <CatalogTaxonomyNodeList
          title={COLUMN_TITLES[NODE_TYPE.GLOBAL]}
          parentId={null}
          parentReady
          selectedId={selectedGlobalId}
          ancestors={[]}
          onSelect={selectNode}
          onCreate={() => startCreate(NODE_TYPE.GLOBAL, null, null, [])}
          emptyText="Немає глобальних категорій"
          productCountsByNodeId={productCountsByNodeId}
          productCountsStatus={productCountsStatus}
        />

        <CatalogTaxonomyNodeList
          title={COLUMN_TITLES[NODE_TYPE.CATEGORY]}
          parentId={selectedGlobalId || null}
          parentReady={categoryParentReady}
          selectedId={selectedCategoryId}
          ancestors={categoryAncestors}
          onSelect={selectNode}
          onCreate={() => {
            if (!categoryParentReady) return
            const parent =
              selectedNode?.type === NODE_TYPE.GLOBAL
                ? selectedNode
                : pathGlobal || { id: selectedGlobalId, type: NODE_TYPE.GLOBAL }
            startCreate(NODE_TYPE.CATEGORY, selectedGlobalId, parent, [])
          }}
          emptyText="Немає категорій"
          parentHint="Оберіть глобальну категорію"
          createDisabledReason={
            categoryCreatePolicy && !categoryCreatePolicy.allowed ? categoryCreatePolicy.message : null
          }
          productCountsByNodeId={productCountsByNodeId}
          productCountsStatus={productCountsStatus}
        />

        <CatalogTaxonomyNodeList
          title={COLUMN_TITLES[NODE_TYPE.SUBCATEGORY]}
          parentId={selectedCategoryId || null}
          parentReady={subcategoryParentReady}
          selectedId={selectedSubcategoryId}
          ancestors={subcategoryAncestors}
          onSelect={selectNode}
          onCreate={() => {
            if (!subcategoryParentReady) return
            const parent =
              selectedNode?.type === NODE_TYPE.CATEGORY
                ? selectedNode
                : pathCategory || { id: selectedCategoryId, type: NODE_TYPE.CATEGORY }
            startCreate(NODE_TYPE.SUBCATEGORY, selectedCategoryId, parent, categoryAncestors)
          }}
          emptyText="Немає підкатегорій"
          parentHint="Оберіть категорію"
          createDisabledReason={
            subcategoryCreatePolicy && !subcategoryCreatePolicy.allowed
              ? subcategoryCreatePolicy.message
              : null
          }
          productCountsByNodeId={productCountsByNodeId}
          productCountsStatus={productCountsStatus}
        />

        <div className={styles.editor}>
          {createState ? (
            <CatalogTaxonomyNodeEditor
              mode="create"
              createType={createState.type}
              createParentId={createState.parentId}
              createParentNode={createState.parentNode}
              createWarning={createState.createWarning}
              onCloseCreate={() => setCreateState(null)}
              onCreated={(id) => {
                setCreateState(null)
                selectNode(id)
              }}
            />
          ) : (
            <CatalogTaxonomyNodeEditor
              mode="edit"
              nodeId={selectedId || ''}
              ancestors={selectedAncestors}
              onDeleted={() => {
                setSelectedNode(null)
                setPathIds(EMPTY_PATH)
                setPathGlobal(null)
                setPathCategory(null)
                router.history.push(CATEGORY_TREE_BASE)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default withRouter(CatalogTaxonomy)
